import { observer, useLocalObservable } from 'mobx-react';
import { useStore } from '../../../store/store';
import IconButton, {
  ICON_BUTTON_TYPES,
  ICON_BUTTON_ICONS
} from '../../../components/buttons/IconButton';
import { SELECTED_PROJECT_TABS } from '../constants';
import { formatNumber, getTooltipId } from '../../../utils/utils';
import { Tooltip } from 'react-tooltip';
import ChipsList from '../../../components/common/ChipsList';
import RegisterStatusBadge from '../../../components/common/RegisterStatusBadge';
import { PUNCTUATION } from '../../../utils/constants/notation';
import {
  buildProfilerProjectGridTemplate,
  PROFILER_PROJECT_VIEW_COLUMNS
} from '../profilerProjectViewConstants';
import { Fragment } from 'react';
import { openInNewTabOnModifier } from '../../../utils/openInNewTab';
import OwnershipSummaryCell from '../../../components/profiler/ownership/OwnershipSummaryCell';
import { resolveOwnershipBasis } from '../../../utils/ownershipCriteria';

const ProfilerSearchResultsTabEntry = observer(
  ({
    entry = {},
    project = {},
    forTab = SELECTED_PROJECT_TABS.RESULTS,
    excludeResult = () => {},
    isExcludingResult = false,
    displayDetails = () => {},
    profilerColumns,
    isProfilerExtendedFinancialsToggled = false,
    // TD-2465 — clickable Industry / Value chain / Products pills (mirrors
    // CompanyResult). `onPillClick(columnKey, value)` toggles the value as a
    // keyword filter; `columnFilters` is the live filter state, read to render
    // the active pill style.
    onPillClick = null,
    columnFilters = null
  }) => {
    const { utilsStore, authStore, profilerProjectStore, profilerAccessStore } = useStore();
    const state = useLocalObservable(() => ({
      nameTooltipId: getTooltipId(),
      grossProfitTooltipId: getTooltipId(),
      basicRevenueTooltipId: getTooltipId(),
      basicEmployeesTooltipId: getTooltipId()
    }));

    const isInWatchlist = profilerProjectStore.watchlist.findIndex((c) => c.id === entry.id) !== -1;

    const watchlistButtonTooltipContent = !profilerAccessStore.canViewProfilerWatchlist ? (
      <>
        Requires Profiler Lite or Profiler Full access.
        <br />
        Contact <a href="mailto:support@transact.digital">support@transact.digital</a> for
        assistance.
      </>
    ) : isInWatchlist ? (
      <>Remove from watchlist</>
    ) : (
      <>Add to watchlist</>
    );
    const watchlistButtonTooltipPosition = !profilerAccessStore.canViewProfilerWatchlist
      ? 'bottom-end'
      : 'bottom-center';
    const watchlistButtonTooltipClickable = !profilerAccessStore.canViewProfilerWatchlist;

    const onWatchlistButtonClick = () => {
      profilerProjectStore.addToWatchlist(entry);
    };

    const openEntryInNewTab = () =>
      utilsStore.openCompanyDetails(entry.id, { ref: 'project results' });

    // Cmd/Ctrl-click opens a new tab; a plain click opens details in place. The
    // right-click "Open in new tab" menu shares the same openEntryInNewTab thunk.
    const onOpenEntryDetails = openInNewTabOnModifier(openEntryInNewTab, () =>
      displayDetails(entry)
    );

    const usersUp = [
      ...new Set(
        project.feedbacks
          ?.filter((fb) => fb.resultId === entry.id && fb.feedback === 'up')
          .map((fb) => {
            const foundUser = authStore.companyUsers.find((u) => u.id === fb.userId);
            if (foundUser) {
              return (
                foundUser.firstName +
                ' ' +
                foundUser.lastName +
                (foundUser.jobPosition ? ` • ${foundUser.jobPosition}` : '')
              );
            }

            return 'unknown';
          })
      )
    ];
    const usersQuestion = [
      ...new Set(
        project.feedbacks
          ?.filter((fb) => fb.resultId === entry.id && fb.feedback === 'question')
          .map((fb) => {
            const foundUser = authStore.companyUsers.find((u) => u.id === fb.userId);
            if (foundUser) {
              return (
                foundUser.firstName +
                ' ' +
                foundUser.lastName +
                (foundUser.jobPosition ? ` • ${foundUser.jobPosition}` : '')
              );
            }

            return 'unknown';
          })
      )
    ];
    const usersDown = [
      ...new Set(
        project.feedbacks
          ?.filter((fb) => fb.resultId === entry.id && fb.feedback === 'down')
          .map((fb) => {
            const foundUser = authStore.companyUsers.find((u) => u.id === fb.userId);
            if (foundUser) {
              return (
                foundUser.firstName +
                ' ' +
                foundUser.lastName +
                (foundUser.jobPosition ? ` • ${foundUser.jobPosition}` : '')
              );
            }

            return 'unknown';
          })
      )
    ];

    const profilerColumnsJSX = {
      [PROFILER_PROJECT_VIEW_COLUMNS.COMPANY_NAME]: (
        <div
          className="item name"
          style={
            utilsStore.windowWidth > 1200
              ? {
                  minWidth: profilerColumns.find(
                    (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.COMPANY_NAME
                  ).minWidth
                }
              : {}
          }
        >
          <div className="label">Company name</div>
          <div className="value">
            <span
              id={state.nameTooltipId}
              className="company-name-link"
              onClick={onOpenEntryDetails}
            >
              {entry.name || PUNCTUATION.EMPTY_VALUE}
            </span>
            <Tooltip
              anchorSelect={`#${state.nameTooltipId}`}
              multiline={true}
              variant="dark"
              place={'bottom-center'}
              opacity={1}
              content={'View details'}
              className={'company-name-view-details-tooltip'}
            />
            <RegisterStatusBadge company={entry} />
          </div>
        </div>
      ),
      [PROFILER_PROJECT_VIEW_COLUMNS.LOCATION]: (
        <div
          className="item headquarter"
          style={
            utilsStore.windowWidth > 1200
              ? {
                  minWidth: profilerColumns.find(
                    (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.LOCATION
                  ).minWidth
                }
              : {}
          }
        >
          <div className="label">Location</div>
          <div className="value">{entry.headquarters || PUNCTUATION.EMPTY_VALUE}</div>
        </div>
      ),
      [PROFILER_PROJECT_VIEW_COLUMNS.COMPANY_DESCRIPTION]: (() => {
        const descriptionTooltipId = getTooltipId();
        return (
          <div
            className="item company-description"
            style={
              utilsStore.windowWidth > 1200
                ? {
                    minWidth: profilerColumns.find(
                      (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.COMPANY_DESCRIPTION
                    )?.minWidth
                  }
                : {}
            }
          >
            <div className="label">Company description</div>
            <div className="value description-with-tooltip">
              {utilsStore.windowWidth > 1200 && !!entry.description && (
                <>
                  <div
                    id={descriptionTooltipId}
                    className="default-black-info-circle-wrap description-similar-companies-button"
                    style={{ flexShrink: 0, marginTop: '2px' }}
                  >
                    <img src={ICON_BUTTON_ICONS.INFO_CIRCLE} />
                  </div>
                  <Tooltip
                    anchorSelect={`#${descriptionTooltipId}`}
                    multiline={true}
                    variant="light"
                    place="bottom-center"
                    opacity={1}
                    content={entry.description}
                    className={'description-similar-companies-tooltip'}
                    clickable
                  />
                </>
              )}
              <div className="short-description" style={{ flex: 1 }}>
                {entry.description || PUNCTUATION.EMPTY_VALUE}
              </div>
            </div>
          </div>
        );
      })(),
      [PROFILER_PROJECT_VIEW_COLUMNS.PRODUCT_PORTFOLIO_DESCRIPTION]: (() => {
        const portfolioTooltipId = getTooltipId();
        return (
          <div
            className="item product-portfolio-description"
            style={
              utilsStore.windowWidth > 1200
                ? {
                    minWidth: profilerColumns.find(
                      (column) =>
                        column.id === PROFILER_PROJECT_VIEW_COLUMNS.PRODUCT_PORTFOLIO_DESCRIPTION
                    )?.minWidth
                  }
                : {}
            }
          >
            <div className="label">Portfolio description</div>
            <div className="value description-with-tooltip">
              {utilsStore.windowWidth > 1200 && !!entry.productPortfolioKeywords && (
                <>
                  <div
                    id={portfolioTooltipId}
                    className="default-black-info-circle-wrap description-tooltip-icon"
                  >
                    <img src={ICON_BUTTON_ICONS.INFO_CIRCLE} />
                  </div>
                  <Tooltip
                    anchorSelect={`#${portfolioTooltipId}`}
                    multiline={true}
                    variant="light"
                    place="bottom-center"
                    opacity={1}
                    content={entry.productPortfolioKeywords?.join(', ')}
                    className={'description-similar-companies-tooltip'}
                    clickable
                  />
                </>
              )}
              <div className="short-description description-text">
                {entry.productPortfolioKeywords?.join(', ') || PUNCTUATION.EMPTY_VALUE}
              </div>
            </div>
          </div>
        );
      })(),
      [PROFILER_PROJECT_VIEW_COLUMNS.KEY_PRODUCTS]: (
        <div
          className="item key-products"
          style={
            utilsStore.windowWidth > 1200
              ? {
                  minWidth: profilerColumns.find(
                    (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.KEY_PRODUCTS
                  )?.minWidth
                }
              : {}
          }
        >
          <div className="label">Key products</div>
          <div className="value">
            <ChipsList list={entry.keyProducts || []} propertyToMap="name" />
          </div>
        </div>
      ),
      [PROFILER_PROJECT_VIEW_COLUMNS.KEY_END_MARKETS]: (
        <div
          className="item key-end-markets"
          style={
            utilsStore.windowWidth > 1200
              ? {
                  minWidth: profilerColumns.find(
                    (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.KEY_END_MARKETS
                  )?.minWidth
                }
              : {}
          }
        >
          <div className="label">Key end-markets</div>
          <div className="value">
            <ChipsList list={entry.businessVerticals || []} propertyToMap="name" />
          </div>
        </div>
      ),
      [PROFILER_PROJECT_VIEW_COLUMNS.INDUSTRY]: (
        <div
          className="item industry"
          style={
            utilsStore.windowWidth > 1200
              ? {
                  minWidth: profilerColumns.find(
                    (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.INDUSTRY
                  ).minWidth
                }
              : {}
          }
        >
          <div className="label">Industry</div>
          <div className="value">
            {/* {entry.industries && entry.industries.length
              ? utilsStore.shortenList(entry.industries, 3).join(', ')
              : PUNCTUATION.EMPTY_VALUE} */}
            <ChipsList
              list={entry.industries}
              onChipClick={onPillClick ? (value) => onPillClick('industries', value) : null}
              activeValues={columnFilters?.industries?.keywords ?? null}
              {...{
                ...(utilsStore.windowWidth > 1200 &&
                !!profilerColumns.find(
                  (column) =>
                    column.id === PROFILER_PROJECT_VIEW_COLUMNS.INDUSTRY && column.selected
                )
                  ? {
                      alignTopMeasurementsEnabled: true,
                      alignTopMeasurementsMinResolutionWidth: 1200,
                      alignTopMeasurementsRowQuerySelector:
                        '.page-projects-listing > .project-details > .lists > .table-responsive > .row',
                      alignTopMeasurementsItemsContainersQuerySelector: '> .item'
                    }
                  : {})
              }}
            />
          </div>
        </div>
      ),
      [PROFILER_PROJECT_VIEW_COLUMNS.VALUE_CHAIN]: (
        <div
          className="item value-chain"
          style={
            utilsStore.windowWidth > 1200
              ? {
                  minWidth: profilerColumns.find(
                    (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.VALUE_CHAIN
                  ).minWidth
                }
              : {}
          }
        >
          <div className="label">Value chain</div>
          <div className="value">
            {/* {entry.valueChain && entry.valueChain.length
              ? utilsStore.shortenList(entry.valueChain, 3).join(', ')
              : PUNCTUATION.EMPTY_VALUE} */}
            <ChipsList
              list={entry.valueChain}
              onChipClick={onPillClick ? (value) => onPillClick('valueChain', value) : null}
              activeValues={columnFilters?.valueChain?.keywords ?? null}
              {...{
                ...(utilsStore.windowWidth > 1200 &&
                !profilerColumns.find(
                  (column) =>
                    column.id === PROFILER_PROJECT_VIEW_COLUMNS.INDUSTRY && column.selected
                )
                  ? {
                      alignTopMeasurementsEnabled: true,
                      alignTopMeasurementsMinResolutionWidth: 1200,
                      alignTopMeasurementsRowQuerySelector:
                        '.page-projects-listing > .project-details > .lists > .table-responsive > .row',
                      alignTopMeasurementsItemsContainersQuerySelector: '> .item'
                    }
                  : {})
              }}
            />
          </div>
        </div>
      ),
      [PROFILER_PROJECT_VIEW_COLUMNS.PRODUCTS]: (
        <div
          className="item products"
          style={
            utilsStore.windowWidth > 1200
              ? {
                  minWidth: profilerColumns.find(
                    (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.PRODUCTS
                  ).minWidth
                }
              : {}
          }
        >
          <div className="label">Products</div>
          <div className="value">
            {/* {entry.products && entry.products.length
              ? utilsStore.shortenList(entry.products, 3).join(', ')
              : PUNCTUATION.EMPTY_VALUE} */}
            <ChipsList
              list={entry.products}
              onChipClick={onPillClick ? (value) => onPillClick('products', value) : null}
              activeValues={columnFilters?.products?.keywords ?? null}
              {...{
                ...(utilsStore.windowWidth > 1200 &&
                !profilerColumns.find(
                  (column) =>
                    column.id === PROFILER_PROJECT_VIEW_COLUMNS.INDUSTRY && column.selected
                ) &&
                !profilerColumns.find(
                  (column) =>
                    column.id === PROFILER_PROJECT_VIEW_COLUMNS.VALUE_CHAIN && column.selected
                )
                  ? {
                      alignTopMeasurementsEnabled: true,
                      alignTopMeasurementsMinResolutionWidth: 1200,
                      alignTopMeasurementsRowQuerySelector:
                        '.page-projects-listing > .project-details > .lists > .table-responsive > .row',
                      alignTopMeasurementsItemsContainersQuerySelector: '> .item'
                    }
                  : {})
              }}
            />
          </div>
        </div>
      ),
      [PROFILER_PROJECT_VIEW_COLUMNS.SALES]: (
        <div
          className="item revenue"
          style={
            utilsStore.windowWidth > 1200
              ? {
                  minWidth: profilerColumns.find(
                    (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.SALES
                  ).minWidth
                }
              : {}
          }
        >
          <div className="label">Sales (€M)</div>
          <div className="value">
            {entry.revenue ? (
              <>
                {!!entry.isRevenueGrossProfit && (
                  <>
                    <div id={state.grossProfitTooltipId} className="default-black-info-circle-wrap">
                      <img src={ICON_BUTTON_ICONS.INFO_CIRCLE} />
                    </div>
                    <Tooltip
                      anchorSelect={`#${state.grossProfitTooltipId}`}
                      multiline={true}
                      variant="dark"
                      place="bottom-center"
                      opacity={1}
                      content={
                        <>
                          This company was included
                          <br />
                          based on reported gross profit,
                          <br />
                          not revenue.
                        </>
                      }
                      className={'default-black-info-tooltip-classname'}
                    />
                  </>
                )}
                <font
                  id={state.basicRevenueTooltipId}
                  style={entry.isRevenueBasic ? { color: '#348cf9' } : {}}
                >
                  {entry.revenue}
                </font>
                <span className="year" style={entry.isRevenueBasic ? { color: '#348cf9' } : {}}>
                  &lsquo;
                  {entry.revenueYear?.toString?.()?.slice?.(-2) || PUNCTUATION.EMPTY_VALUE}
                </span>
                {!!entry.isRevenueBasic && (
                  <Tooltip
                    anchorSelect={`#${state.basicRevenueTooltipId}`}
                    multiline={true}
                    variant="dark"
                    place="bottom-center"
                    opacity={1}
                    content={<>Based on self-disclosure or estimate</>}
                    className={'default-black-info-tooltip-classname'}
                  />
                )}
              </>
            ) : (
              PUNCTUATION.EMPTY_VALUE
            )}
          </div>
        </div>
      ),
      [PROFILER_PROJECT_VIEW_COLUMNS.EMPLOYEES]: (
        <div
          className="item employees"
          style={
            utilsStore.windowWidth > 1200
              ? {
                  minWidth: profilerColumns.find(
                    (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.EMPLOYEES
                  ).minWidth
                }
              : {}
          }
        >
          <div className="label">Employees</div>
          <div className="value">
            {!isNaN(entry.employees) && entry.employees !== null ? (
              <>
                <font
                  id={state.basicEmployeesTooltipId}
                  style={entry.isEmployeesBasic ? { color: '#348cf9' } : {}}
                >
                  {formatNumber((+entry.employees).toFixed(2))}
                </font>
                <span className="year" style={entry.isEmployeesBasic ? { color: '#348cf9' } : {}}>
                  &lsquo;
                  {entry.employeesYear?.toString?.()?.slice?.(-2) || PUNCTUATION.EMPTY_VALUE}
                </span>
                {!!entry.isEmployeesBasic && (
                  <Tooltip
                    anchorSelect={`#${state.basicEmployeesTooltipId}`}
                    multiline={true}
                    variant="dark"
                    place="bottom-center"
                    opacity={1}
                    content={<>Based on self-disclosure or estimate</>}
                    className={'default-black-info-tooltip-classname'}
                  />
                )}
              </>
            ) : (
              PUNCTUATION.EMPTY_VALUE
            )}
          </div>
        </div>
      ),
      [PROFILER_PROJECT_VIEW_COLUMNS.CAGR3Y]: (
        <div
          className="item cagr"
          style={
            utilsStore.windowWidth > 1200
              ? {
                  minWidth: profilerColumns.find(
                    (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.CAGR3Y
                  ).minWidth
                }
              : {}
          }
        >
          <div className="label">CAGR 3Y (%)</div>
          <div className="value">{entry.cagr3Y ? entry.cagr3Y : PUNCTUATION.EMPTY_VALUE}</div>
        </div>
      ),
      [PROFILER_PROJECT_VIEW_COLUMNS.CAGR4Y]: (
        <div
          className="item cagr"
          style={
            utilsStore.windowWidth > 1200
              ? {
                  minWidth: profilerColumns.find(
                    (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.CAGR4Y
                  ).minWidth
                }
              : {}
          }
        >
          <div className="label">CAGR 4Y (%)</div>
          <div className="value">{entry.cagr4Y ? entry.cagr4Y : PUNCTUATION.EMPTY_VALUE}</div>
        </div>
      ),
      [PROFILER_PROJECT_VIEW_COLUMNS.EBIT_MARGIN]: (
        <div
          className="item ebit-margin"
          style={
            utilsStore.windowWidth > 1200
              ? {
                  // Optional: column may be omitted for Profiler Lite (TD-2499).
                  minWidth: profilerColumns.find(
                    (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.EBIT_MARGIN
                  )?.minWidth
                }
              : {}
          }
        >
          <div className="label">Ebit Margin (%)</div>
          <div className="value">
            {entry.ebitPercentage ? entry.ebitPercentage : PUNCTUATION.EMPTY_VALUE}
          </div>
        </div>
      ),
      [PROFILER_PROJECT_VIEW_COLUMNS.SITUATION]: (
        <div
          className="item situation"
          style={
            utilsStore.windowWidth > 1200
              ? {
                  minWidth: profilerColumns.find(
                    (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.SITUATION
                  ).minWidth
                }
              : {}
          }
        >
          <div className="label">Situation</div>
          <div className="value">
            <ChipsList
              list={
                Array.isArray(entry.situations)
                  ? entry.situations
                  : entry.situation
                    ? [entry.situation]
                    : []
              }
              {...{
                ...(utilsStore.windowWidth > 1200
                  ? {
                      alignTopMeasurementsEnabled: true,
                      alignTopMeasurementsMinResolutionWidth: 1200,
                      alignTopMeasurementsRowQuerySelector:
                        '.page-projects-listing > .project-details > .lists > .table-responsive > .row',
                      alignTopMeasurementsItemsContainersQuerySelector: '> .item'
                    }
                  : {})
              }}
            />
          </div>
        </div>
      ),
      [PROFILER_PROJECT_VIEW_COLUMNS.SHAREHOLDERS]: (
        <div
          className="item shareholders"
          style={
            utilsStore.windowWidth > 1200
              ? {
                  minWidth: profilerColumns.find(
                    (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.SHAREHOLDERS
                  )?.minWidth
                }
              : {}
          }
        >
          <div className="label">Shareholders</div>
          <div className="value">
            <OwnershipSummaryCell
              summary={entry.ownershipSummary}
              basis={resolveOwnershipBasis(project?.fields?.ownershipBasis)}
              searchedBasis={resolveOwnershipBasis(project?.fields?.ownershipBasis)}
              searchedStructures={project?.fields?.ownershipStructures || []}
            />
          </div>
        </div>
      ),
      [PROFILER_PROJECT_VIEW_COLUMNS.FEEDBACK]: (
        <div
          className="item feedback desktop-only"
          style={{
            minWidth: profilerColumns.find(
              (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.FEEDBACK
            ).minWidthResult
          }}
        >
          <IconButton
            type={ICON_BUTTON_TYPES.GREEN}
            icon={ICON_BUTTON_ICONS.HAND_THUMBS_UP}
            innerText={
              (project?.feedbacks?.filter?.(
                (fb) => fb.resultId === entry.id && fb.feedback === 'up'
              )?.length || 0) + ''
            }
            showTooltipArrow={false}
            tooltipVariant="light"
            tooltipPosition="bottom-end"
            tooltipClassName="popup-feedback popup-feedback-up"
            tooltipClickable
            tooltipContent={
              usersUp.length ? (
                <div className="ppfp-scrollable-body">
                  {usersUp.map((username, idx) => {
                    return (
                      <div className="ppfp-item" key={idx}>
                        {username}
                      </div>
                    );
                  })}
                </div>
              ) : null
            }
            wrapClassName="feedback force-default-cursor"
          />
          <IconButton
            type={ICON_BUTTON_TYPES.YELLOW}
            icon={ICON_BUTTON_ICONS.QUESTION_LG}
            innerText={
              (project?.feedbacks?.filter?.(
                (fb) => fb.resultId === entry.id && fb.feedback === 'question'
              )?.length || 0) + ''
            }
            showTooltipArrow={false}
            tooltipVariant="light"
            tooltipPosition="bottom-end"
            tooltipClassName="popup-feedback popup-feedback-question"
            tooltipClickable
            tooltipContent={
              usersQuestion.length ? (
                <div className="ppfp-scrollable-body">
                  {usersQuestion.map((username, idx) => {
                    return (
                      <div className="ppfp-item" key={idx}>
                        {username}
                      </div>
                    );
                  })}
                </div>
              ) : null
            }
            wrapClassName="feedback force-default-cursor"
          />
          <IconButton
            type={ICON_BUTTON_TYPES.RED}
            icon={ICON_BUTTON_ICONS.HAND_THUMBS_DOWN}
            innerText={
              (project?.feedbacks?.filter?.(
                (fb) => fb.resultId === entry.id && fb.feedback === 'down'
              )?.length || 0) + ''
            }
            showTooltipArrow={false}
            tooltipVariant="light"
            tooltipPosition="bottom-end"
            tooltipClassName="popup-feedback popup-feedback-down"
            tooltipClickable
            tooltipContent={
              usersDown.length ? (
                <div className="ppfp-scrollable-body">
                  {usersDown.map((username, idx) => {
                    return (
                      <div className="ppfp-item" key={idx}>
                        {username}
                      </div>
                    );
                  })}
                </div>
              ) : null
            }
            wrapClassName="feedback force-default-cursor"
          />
        </div>
      ),
      [PROFILER_PROJECT_VIEW_COLUMNS.ACTIONS]: (
        <div
          className="item actions profiler-search-actions desktop-only"
          style={{
            minWidth: profilerColumns.find(
              (column) => column.id === PROFILER_PROJECT_VIEW_COLUMNS.ACTIONS
            ).minWidthResult
          }}
        >
          <IconButton
            icon={ICON_BUTTON_ICONS.VIEW_DETAILS_CUSTOM1}
            type={ICON_BUTTON_TYPES.BLUE}
            onClick={onOpenEntryDetails}
            tooltipText="View details"
            linkContextMenu={[
              {
                icon: ICON_BUTTON_ICONS.BOX_ARROW_UP_RIGHT,
                text: 'Open in new tab',
                onClick: () => {
                  utilsStore.openCompanyDetails(entry.id, { ref: 'project results' });
                }
              }
            ]}
          />
          <IconButton
            wrapClassName="project-results-watchlist-action-btn"
            type={ICON_BUTTON_TYPES.BLUE}
            icon={ICON_BUTTON_ICONS.WATCHLIST}
            disabled={
              !!profilerProjectStore.companiesInWatchlistAction[entry.id] ||
              !profilerAccessStore.canViewProfilerWatchlist
            }
            tooltipContent={watchlistButtonTooltipContent}
            tooltipPosition={watchlistButtonTooltipPosition}
            tooltipClickable={watchlistButtonTooltipClickable}
            filled={isInWatchlist}
            onClick={onWatchlistButtonClick}
          />
          {forTab === SELECTED_PROJECT_TABS.RESULTS && (
            <IconButton
              wrapClassName="project-results-exclude-button"
              type={ICON_BUTTON_TYPES.RED}
              icon={ICON_BUTTON_ICONS.X_LG}
              tooltipText="Exclude result"
              disabled={isExcludingResult}
              onClick={() => excludeResult(entry, true)}
            />
          )}
          {forTab === SELECTED_PROJECT_TABS.EXCLUDED && (
            <IconButton
              wrapClassName="project-results-exclude-button"
              type={ICON_BUTTON_TYPES.BLUE}
              icon={ICON_BUTTON_ICONS.ARROW_REPEAT}
              tooltipText="Restore result"
              disabled={isExcludingResult}
              onClick={() => excludeResult(entry, false)}
            />
          )}
        </div>
      )
    };

    return (
      <div
        className="row profiler-search-cols"
        style={
          utilsStore.windowWidth > 1200
            ? {
                // TD-2733 — built by the same helper as the detached column header, which
                // used to carry its own copy of this: they had drifted by one column, and
                // their `fr` tracks resolved differently because each grid sized to its own
                // content.
                gridTemplateColumns: buildProfilerProjectGridTemplate(profilerColumns, {
                  windowWidth: utilsStore.windowWidth,
                  isProfilerExtendedFinancialsToggled,
                  forRows: true
                }),
                marginTop: '8px'
              }
            : { marginTop: '8px' }
        }
      >
        {entry.showScoring && (
          <>
            <div
              className="result-scoring-badge noselect"
              id={`result-scoring-badge-${entry.id}`}
              style={{ backgroundColor: entry.scoreSecondaryColor }}
            >
              <div className="score-icon-wrap">
                <img
                  src={ICON_BUTTON_ICONS.EXCLAMATAION_CIRCLE}
                  style={{ filter: entry.scoreIconFilter }}
                />
              </div>
              <div className="score-label" style={{ color: entry.scorePrimaryColor }}>
                {entry.scoreLabel}
              </div>
            </div>
            <Tooltip
              anchorSelect={`#result-scoring-badge-${entry.id}`}
              multiline={true}
              variant="light"
              place="bottom-end"
              opacity={1}
              content={entry.scoreTooltipText}
              className={entry.scoreTooltipClassName}
              clickable
            />
          </>
        )}
        <div className="item feedback mobile-only">
          <IconButton
            type={ICON_BUTTON_TYPES.GREEN}
            icon={ICON_BUTTON_ICONS.HAND_THUMBS_UP}
            innerText={
              (project?.feedbacks?.filter?.(
                (fb) => fb.resultId === entry.id && fb.feedback === 'up'
              )?.length || 0) + ''
            }
            showTooltipArrow={false}
            tooltipVariant="light"
            tooltipPosition="bottom-end"
            tooltipClassName="popup-feedback popup-feedback-up"
            tooltipClickable
            tooltipContent={
              usersUp.length ? (
                <div className="ppfp-scrollable-body">
                  {usersUp.map((username, idx) => {
                    return (
                      <div className="ppfp-item" key={idx}>
                        {username}
                      </div>
                    );
                  })}
                </div>
              ) : null
            }
            wrapClassName="feedback"
          />
          <IconButton
            type={ICON_BUTTON_TYPES.YELLOW}
            icon={ICON_BUTTON_ICONS.QUESTION_LG}
            innerText={
              (project?.feedbacks?.filter?.(
                (fb) => fb.resultId === entry.id && fb.feedback === 'question'
              )?.length || 0) + ''
            }
            showTooltipArrow={false}
            tooltipVariant="light"
            tooltipPosition="bottom-end"
            tooltipClassName="popup-feedback popup-feedback-question"
            tooltipClickable
            tooltipContent={
              usersQuestion.length ? (
                <div className="ppfp-scrollable-body">
                  {usersQuestion.map((username, idx) => {
                    return (
                      <div className="ppfp-item" key={idx}>
                        {username}
                      </div>
                    );
                  })}
                </div>
              ) : null
            }
            wrapClassName="feedback"
          />
          <IconButton
            type={ICON_BUTTON_TYPES.RED}
            icon={ICON_BUTTON_ICONS.HAND_THUMBS_DOWN}
            innerText={
              (project?.feedbacks?.filter?.(
                (fb) => fb.resultId === entry.id && fb.feedback === 'down'
              )?.length || 0) + ''
            }
            showTooltipArrow={false}
            tooltipVariant="light"
            tooltipPosition="bottom-end"
            tooltipClassName="popup-feedback popup-feedback-down"
            tooltipClickable
            tooltipContent={
              usersDown.length ? (
                <div className="ppfp-scrollable-body">
                  {usersDown.map((username, idx) => {
                    return (
                      <div className="ppfp-item" key={idx}>
                        {username}
                      </div>
                    );
                  })}
                </div>
              ) : null
            }
            wrapClassName="feedback"
          />
        </div>

        {profilerColumns
          .filter((column) =>
            utilsStore.windowWidth > 1200
              ? utilsStore.windowWidth < 1536 && isProfilerExtendedFinancialsToggled
                ? ![
                    PROFILER_PROJECT_VIEW_COLUMNS.LOCATION,
                    PROFILER_PROJECT_VIEW_COLUMNS.INDUSTRY,
                    PROFILER_PROJECT_VIEW_COLUMNS.VALUE_CHAIN,
                    PROFILER_PROJECT_VIEW_COLUMNS.PRODUCTS
                  ].includes(column.id)
                : column.selected
              : column.selected
          )
          .map((column) => {
            return <Fragment key={column.id}>{profilerColumnsJSX[column.id]}</Fragment>;
          })}

        <div className="actions-mobile mobile-only">
          <IconButton
            innerText="View details"
            onClick={onOpenEntryDetails}
            type={ICON_BUTTON_TYPES.BLUE}
            filled
            wrapClassName="view-details-search-results"
            linkContextMenu={[
              {
                icon: ICON_BUTTON_ICONS.BOX_ARROW_UP_RIGHT,
                text: 'Open in new tab',
                onClick: () => {
                  utilsStore.openCompanyDetails(entry.id, { ref: 'project results' });
                }
              }
            ]}
          />
          <IconButton
            wrapClassName="project-results-watchlist-action-btn"
            type={ICON_BUTTON_TYPES.BLUE}
            icon={ICON_BUTTON_ICONS.WATCHLIST}
            disabled={
              profilerProjectStore.isLoadingWatchlist ||
              profilerProjectStore.initialLoadWatchlist ||
              !!profilerProjectStore.companiesInWatchlistAction[entry.id] ||
              !profilerAccessStore.canViewProfilerWatchlist
            }
            tooltipContent={watchlistButtonTooltipContent}
            tooltipPosition={watchlistButtonTooltipPosition}
            tooltipClickable={watchlistButtonTooltipClickable}
            filled={isInWatchlist}
            onClick={onWatchlistButtonClick}
          />
          {forTab === SELECTED_PROJECT_TABS.RESULTS && (
            <IconButton
              wrapClassName="project-results-exclude-button"
              type={ICON_BUTTON_TYPES.RED}
              icon={ICON_BUTTON_ICONS.X_LG}
              tooltipText="Exclude result"
              disabled={isExcludingResult}
              onClick={() => excludeResult(entry, true)}
            />
          )}
          {forTab === SELECTED_PROJECT_TABS.EXCLUDED && (
            <IconButton
              wrapClassName="project-results-exclude-button"
              type={ICON_BUTTON_TYPES.BLUE}
              icon={ICON_BUTTON_ICONS.ARROW_REPEAT}
              tooltipText="Restore result"
              disabled={isExcludingResult}
              onClick={() => excludeResult(entry, false)}
            />
          )}
        </div>
      </div>
    );
  }
);

export default ProfilerSearchResultsTabEntry;
